import type {
  AmbiguousGeomRoleCandidate,
  MJCFParseResult,
  ParsedBody,
  ParsedGeom,
} from "./mjcfParser";
import { generateUuid } from "../utils/id";

export type AmbiguousGeomRole = "visual" | "both" | "collision";
export type AmbiguousGeomRoleSelections = Record<number, AmbiguousGeomRole>;

export function getAmbiguousGeomRoleCandidatesByWarning(
  parsedResult: MJCFParseResult,
  warnings: string[]
): AmbiguousGeomRoleCandidate[] {
  const candidates = Array.isArray(parsedResult.ambiguousGeomRoleCandidates)
    ? parsedResult.ambiguousGeomRoleCandidates
    : [];
  if (candidates.length === 0 || warnings.length === 0) return [];
  const byWarning = new Map<string, AmbiguousGeomRoleCandidate[]>();
  candidates.forEach((candidate) => {
    const key = String(candidate?.warning || "").trim();
    if (!key) return;
    const bucket = byWarning.get(key) || [];
    bucket.push(candidate);
    byWarning.set(key, bucket);
  });
  return warnings
    .map((warning) => byWarning.get(warning)?.shift())
    .filter(Boolean) as AmbiguousGeomRoleCandidate[];
}

export function applyAmbiguousGeomRoleSelections(
  parsedResult: MJCFParseResult,
  candidates: AmbiguousGeomRoleCandidate[],
  roles?: AmbiguousGeomRoleSelections
): void {
  if (!roles || candidates.length === 0) return;
  const visualGeomUuids = new Set(
    Object.entries(roles)
      .filter(([, role]) => role === "visual")
      .map(([index]) => candidates[Number(index)]?.geomUuid)
      .filter(Boolean)
  );
  const bothGeomUuids = new Set(
    Object.entries(roles)
      .filter(([, role]) => role === "both")
      .map(([index]) => candidates[Number(index)]?.geomUuid)
      .filter(Boolean)
  );
  if (visualGeomUuids.size === 0 && bothGeomUuids.size === 0) return;
  Object.values(parsedResult.bodies || {}).forEach((body: ParsedBody) => {
    if (!body || !Array.isArray(body.geoms)) return;
    const nextGeoms: ParsedGeom[] = [];
    body.geoms.forEach((geom: ParsedGeom) => {
      if (!geom) {
        nextGeoms.push(geom);
        return;
      }
      const geomUuid = String(geom.uuid || "");
      if (visualGeomUuids.has(geomUuid)) {
        makeGeomVisualOnly(geom);
        nextGeoms.push(geom);
        return;
      }
      if (bothGeomUuids.has(geomUuid)) {
        const visualUuid = generateUuid();
        const visualGeom = {
          ...geom,
          uuid: visualUuid,
          name: geom.name ? `${geom.name}_visual` : undefined,
          pairedGeomUUID: geom.uuid,
        };
        makeGeomVisualOnly(visualGeom);
        geom.pairedGeomUUID = visualUuid;
        nextGeoms.push(visualGeom, geom);
        return;
      }
      nextGeoms.push(geom);
    });
    body.geoms = nextGeoms;
  });
}

function makeGeomVisualOnly(geom: ParsedGeom): void {
  geom.visualOnly = true;
  geom.contactCategory = 0;
  geom.contactAffinity = 0;
  geom.contactDimensions = undefined;
  geom.mass = undefined;
  geom.density = undefined;
  geom.frictionCoefficients = undefined;
  geom.solverReference = undefined;
  geom.solverImpedance = undefined;
}
